import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Shield, Heart, Users, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const CEO_PHOTO = "https://customer-assets.emergentagent.com/job_e1b79d0e-9ea4-4f7b-8d98-a9785f01758d/artifacts/tple5nn3_ChatGPT%20Image%20Mar%202%2C%202026%2C%2002_18_19%20PM.png";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const values = [
  {
    icon: Shield,
    title: "Integrity",
    description: "We believe in doing what's right, even when no one is watching. Our commitment to honesty and transparency guides every interaction."
  },
  {
    icon: Users,
    title: "Reliance",
    description: "When you work with us, you can count on us to be there. We follow through on our commitments and keep you informed every step of the way."
  },
  {
    icon: Heart,
    title: "Compassion",
    description: "We understand that losing a home is one of life's most difficult experiences. We approach every case with empathy and genuine care."
  }
];

export default function AboutPage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 hero-gradient">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-emerald-600 font-medium uppercase tracking-wider text-sm">
                About Us
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mt-2 mb-6" data-testid="about-title">
                Meet the Team Behind <span className="text-emerald-600">Cornerstone</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                At Cornerstone Refund, we're more than just a business. We're a team dedicated to 
                helping people recover what's rightfully theirs, with integrity, reliance, and compassion.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-emerald-100 rounded-3xl transform -rotate-3"></div>
                <img 
                  src={CEO_PHOTO}
                  alt="Michael Ernest, CEO" 
                  className="relative rounded-2xl shadow-2xl w-full max-w-md mx-auto object-cover"
                  data-testid="about-ceo-photo"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CEO Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Michael Ernest
              </h2>
              <p className="text-emerald-600 font-medium text-lg">
                Chief Executive Officer
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <blockquote className="quote-block text-center max-w-3xl mx-auto mb-12" data-testid="ceo-quote">
                <Quote className="w-12 h-12 text-amber-400 mx-auto mb-6" strokeWidth={1} />
                <p className="font-serif text-2xl md:text-3xl italic text-slate-700 leading-relaxed">
                  "Perseverance and determination usually wins most of the battles and are the key to success"
                </p>
                <footer className="mt-6 text-slate-500 not-italic">
                  — Michael Ernest
                </footer>
              </blockquote>

              <div className="prose prose-slate max-w-none">
                <p className="text-slate-600 leading-relaxed text-lg mb-6">
                  Michael Ernest founded Cornerstone Refund with a simple mission: to help property 
                  owners recover money that's rightfully theirs. With years of experience in real 
                  estate and financial services, Michael recognized that many people were unaware 
                  of surplus funds from foreclosure sales — or found the recovery process too 
                  daunting to navigate alone.
                </p>
                <p className="text-slate-600 leading-relaxed text-lg mb-6">
                  Under Michael's leadership, Cornerstone has helped countless families in California 
                  and Arizona recover funds they didn't know they were owed. His approach is personal 
                  and hands-on — clients often describe working with Mike as "like working with a 
                  friend you've known for years."
                </p>
                <p className="text-slate-600 leading-relaxed text-lg">
                  Based in Phoenix, Arizona, Michael and his team are committed to treating every 
                  client with the respect and care they deserve. Because at Cornerstone, it's not 
                  just about recovering funds — it's about restoring peace of mind.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-emerald-600 font-medium uppercase tracking-wider text-sm">
              What We Stand For
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
              Our Core Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="card-hover border-slate-100 h-full" data-testid={`about-value-${index}`}>
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <value.icon className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Our Office
              </h2>
              <p className="text-slate-600 leading-relaxed text-lg mb-6">
                Cornerstone Refund is headquartered in Phoenix, Arizona, allowing us to serve 
                clients throughout Arizona and California with personalized attention.
              </p>
              
              <div className="bg-slate-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-slate-900 mb-2">Cornerstone Refund</h4>
                <p className="text-slate-600">
                  2345 E. Thomas Rd Ste 100<br />
                  #510<br />
                  Phoenix, AZ 85016
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:405-754-2183">
                  <Button className="btn-primary w-full sm:w-auto">
                    <Phone className="mr-2 w-5 h-5" />
                    405-754-2183
                  </Button>
                </a>
                <Link to="/contact">
                  <Button variant="outline" className="btn-secondary w-full sm:w-auto">
                    Contact Us
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="bg-slate-900 text-white border-0">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold mb-4">Why Choose Cornerstone?</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold">1</span>
                      </div>
                      <p className="text-slate-300">
                        <strong className="text-white">No Upfront Fees</strong> — We only get paid when you get paid
                      </p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold">2</span>
                      </div>
                      <p className="text-slate-300">
                        <strong className="text-white">Personal Service</strong> — Work directly with our team, not a call center
                      </p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold">3</span>
                      </div>
                      <p className="text-slate-300">
                        <strong className="text-white">Proven Experience</strong> — Extensive knowledge of CA & AZ procedures
                      </p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold">4</span>
                      </div>
                      <p className="text-slate-300">
                        <strong className="text-white">Transparent Process</strong> — Regular updates and clear communication
                      </p>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
